import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { BookingApproveFormFieldsEnum } from "@ryde/controls";
import { useCrudEditContext } from "@ryde/redux";
import {
  BookingConflictErrorType,
  BookingFormFields,
  errorsCrudContentAtomFamily,
  IExtendableDto,
} from "@ryde/shared";

import { useBookingEditInner } from "./use-booking-edit-inner";

export const useBookingEditEffects = () => {
  const { entity } = useCrudEditContext<IExtendableDto>();

  const { t } = useTranslation();
  const serverErrors = useRecoilValue(errorsCrudContentAtomFamily(entity));

  const {
    formState: { defaultValues },
    watch,
    setError,
    clearErrors,
  } = useFormContext();

  const directionWatchValue = watch(BookingFormFields.direction);
  const directionWatchValueRef = useRef(null);
  const shiftWatchValue = watch(BookingFormFields.shiftId);

  useEffect(() => {
    if (!directionWatchValue) {
      return;
    }

    if (directionWatchValueRef.current) {
      directionWatchValueRef.current = directionWatchValue;
    } else if (
      directionWatchValue !== defaultValues?.[BookingFormFields.direction] ||
      shiftWatchValue !== defaultValues?.[BookingFormFields.shiftId]
    ) {
      directionWatchValueRef.current = directionWatchValue;
    }
  }, [defaultValues, directionWatchValue, shiftWatchValue]);

  const { handleDirectionUpdate } = useBookingEditInner();

  const isIneligibleErrors = useMemo(() => {
    if (!serverErrors) {
      return false;
    }

    return (
      serverErrors?.includes(BookingConflictErrorType.passengerIneligible) ||
      serverErrors?.includes(
        BookingConflictErrorType.passengerTimeIneligible,
      ) ||
      serverErrors?.includes(
        BookingConflictErrorType.passengerLocationIneligible,
      )
    );
  }, [serverErrors]);
  const approverValue = watch(BookingApproveFormFieldsEnum.APPROVER);
  const approverReasonValue = watch(
    BookingApproveFormFieldsEnum.APPROVAL_REASON_ID,
  );

  useEffect(() => {
    const currentDirection = directionWatchValueRef?.current;

    if (currentDirection) {
      handleDirectionUpdate({
        directionWatch: currentDirection,
        checkShiftAndChangeDirection: true,
      });
    }
  }, [directionWatchValue, handleDirectionUpdate, shiftWatchValue]);

  useEffect(() => {
    if (!approverReasonValue && isIneligibleErrors) {
      // Hack to add error after scheme validation
      setTimeout(() => {
        setError(BookingApproveFormFieldsEnum.APPROVAL_REASON_ID, {
          message: t("errors.requiredField", {
            fieldName: t("booking.formLabel.approvalReasonId"),
          }),
        });
      }, 10);
    } else {
      clearErrors(BookingApproveFormFieldsEnum.APPROVAL_REASON_ID);
    }
  }, [approverReasonValue, clearErrors, isIneligibleErrors, setError, t]);

  useEffect(() => {
    if (!approverValue && isIneligibleErrors) {
      // Hack to add error after scheme validation
      setTimeout(() => {
        setError(BookingApproveFormFieldsEnum.APPROVER, {
          message: t("errors.requiredField", {
            fieldName: t("booking.formLabel.approver"),
          }),
        });
      }, 10);
    } else {
      clearErrors(BookingApproveFormFieldsEnum.APPROVER);
    }
  }, [approverValue, clearErrors, isIneligibleErrors, setError, t]);

  return { isIneligibleErrors };
};
